import {
  BackEndValueType,
  ECalculatedFieldFunction,
  EDateDiffPart,
  EGenericSelectDataGroupBy,
  ESortType,
  ESymbolCategory,
  IDatabaseTableModel,
  IGenericSelectCalculatedField,
  IGenericSelectDataField,
  IGenericSelectTablesModel,
  ISymbol
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaH7 from "../../../../ui-kit/typography/H7/H7";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import { SymbolPicker } from "../../../right-panel/properties-panel/symbol-picker";

export interface ICalculatedFieldsItemState {
  isEditing: boolean;
  localData?: IGenericSelectCalculatedField;
}

export interface ICalculatedFieldsItemProps {
  data: IGenericSelectCalculatedField;
  updateData: (newData: IGenericSelectCalculatedField) => void;
  tables: IDatabaseTableModel[];
  selectedTables: IGenericSelectTablesModel[];
  delete: () => void;
  getTypeIcon: (type: string) => React.ReactElement;
  usedFunctions: ECalculatedFieldFunction[];
  allCalculatedFields?: IGenericSelectCalculatedField[];
  currentIndex?: number;
  defaultEditing?: boolean;
  onEditEnd?: () => void;
  onCollapse?: () => void;
}

const groupByOptions = [
  { value: EGenericSelectDataGroupBy.Count, label: "Count" },
  { value: EGenericSelectDataGroupBy.Min, label: "Min" },
  { value: EGenericSelectDataGroupBy.Max, label: "Max" }
];

const datePartOptions = [
  { value: EDateDiffPart.Day, label: "Day" },
  { value: EDateDiffPart.Month, label: "Month" },
  { value: EDateDiffPart.Year, label: "Year" },
  { value: EDateDiffPart.Hour, label: "Hour" },
  { value: EDateDiffPart.Minute, label: "Minute" },
  { value: EDateDiffPart.Second, label: "Second" },
  { value: EDateDiffPart.Week, label: "Week" }
];

const sortTypeOptions = [
  { value: ESortType.ascending, label: "ASC" },
  { value: ESortType.descending, label: "DESC" }
];

class CalculatedFieldsItem extends React.PureComponent<ICalculatedFieldsItemProps, ICalculatedFieldsItemState> {
  constructor(props: ICalculatedFieldsItemProps) {
    super(props);
    this.state = {
      isEditing: props.defaultEditing || false,
      localData: props.defaultEditing ? _.cloneDeep(props.data) : undefined
    };
  }

  componentDidUpdate(prevProps: ICalculatedFieldsItemProps) {
    if (this.props.defaultEditing !== prevProps.defaultEditing && this.props.defaultEditing) {
      this.setState({ isEditing: true, localData: _.cloneDeep(this.props.data) });
    }
  }

  getData = () => {
    return this.state.isEditing && this.state.localData ? this.state.localData : this.props.data;
  };

  handleEnterEdit = () => {
    this.setState({ isEditing: true, localData: _.cloneDeep(this.props.data) });
  };

  handleCancel = () => {
    this.setState({ isEditing: false, localData: undefined });
    this.props.onEditEnd?.();
    this.props.onCollapse?.();
  };

  handleSave = () => {
    if (this.state.localData) {
      const data = _.cloneDeep(this.state.localData);
      data.isCollapsed = true;
      this.props.updateData(data);
      this.setState({ isEditing: false, localData: undefined });
      this.props.onEditEnd?.();
    }
  };

  supportsGroupBy = (func?: ECalculatedFieldFunction): boolean => {
    if (func === ECalculatedFieldFunction.RowNumber) return false;
    if (func === ECalculatedFieldFunction.DateDiff) return true;
    return false;
  };

  getTypeName = (sqlDataType: string, dataLength: number | any) => {
    let potantialType = KMainFunctions.getTypeNameBySqlDataType(sqlDataType);
    if (potantialType == "Unknown") {
      potantialType = KMainFunctions.getTypeNameBySqlDataType(`${sqlDataType}(${dataLength})`);
    }
    return potantialType;
  };

  // Get value type for SymbolPicker based on field
  getFieldValueType = (field: IGenericSelectDataField): BackEndValueType => {
    if (field.colName && field.tableName) {
      const targetTable = this.props.tables.find((x) => x.name === field.tableName);
      const targetColumn = targetTable?.columns.find((x) => x.name === field.colName);
      return (targetColumn?.kuikaDataType || "Unknown") as BackEndValueType;
    }
    return "Unknown" as BackEndValueType;
  };

  // Get or create symbol for field
  getFieldSymbol = (field: IGenericSelectDataField): ISymbol => {
    const tableName = field.tableName?.trim();
    const colName = field.colName?.trim();

    if (tableName && tableName !== "" && colName && colName !== "") {
      const tableDisplayName = field.tableAlias?.trim() || tableName;
      const fieldDisplay = `${tableDisplayName}.${colName}`;
      const symbol = {
        category: ESymbolCategory.actionField,
        label: fieldDisplay,
        description: fieldDisplay,
        fieldName: fieldDisplay,
        currentType: this.getFieldValueType(field)
      } as ISymbol;

      if (!symbol.label) symbol.label = fieldDisplay;
      if (!symbol.description) symbol.description = fieldDisplay;

      return symbol;
    }

    if (field.paramSymbol && (field.paramSymbol.label || field.paramSymbol.description)) {
      const symbol = _.cloneDeep(field.paramSymbol);
      if (!symbol.label && symbol.description) symbol.label = symbol.description;
      if (!symbol.description && symbol.label) symbol.description = symbol.label;
      return symbol;
    }

    return {
      category: ESymbolCategory.fixed,
      label: "Fixed Value",
      description: "Fixed Value"
    } as ISymbol;
  };

  handleFieldSymbolSelected = (
    symbol: ISymbol,
    fieldIndex: number,
    fieldArray: "fields" | "partitionByFields" | "orderByFields"
  ) => {
    const data = _.cloneDeep(this.getData());
    const fields = data[fieldArray] as IGenericSelectDataField[];
    const field = fields[fieldIndex];

    if (symbol.category === ESymbolCategory.actionField && symbol.fieldName) {
      field.tableName = "";
      field.tableAlias = "";
      field.tableId = null;
      field.tableSchema = "";
      field.colName = "";
      field.colSqlDataType = "";
      field.paramSymbol = undefined;

      const parts = symbol.fieldName.split(".");
      if (parts.length === 2) {
        const [tableRef, colName] = parts;
        const selectedTable = this.props.selectedTables.find((t) => t.name === tableRef || t.alias === tableRef);

        if (selectedTable) {
          const tableModel = this.props.tables.find((t) => t.name === selectedTable.name);
          if (tableModel) {
            const column = tableModel.columns.find((c) => c.name === colName);
            if (column) {
              field.tableName = selectedTable.name;
              field.tableAlias = selectedTable.alias;
              field.tableId = tableModel.id || null;
              field.tableSchema = tableModel.schema || "";
              field.colName = colName;
              field.colSqlDataType = this.getTypeName(column.sqlDataType, column.maxLength);

              const tableDisplayName = field.tableAlias || field.tableName;
              const fieldDisplay = `${tableDisplayName}.${field.colName}`;
              const valueType = this.getFieldValueType(field);

              field.paramSymbol = {
                category: ESymbolCategory.actionField,
                label: fieldDisplay,
                description: fieldDisplay,
                fieldName: fieldDisplay,
                currentType: valueType
              } as ISymbol;

              if (!field.validations) field.validations = {};
              field.validations.paramSymbol = true;
              field.validations.tableName = true;
              field.validations.colName = true;

              this.setState({ localData: data });
              return;
            }
          }
        }
      }

      field.paramSymbol = _.cloneDeep(symbol);
      if (!field.validations) field.validations = {};
      field.validations.paramSymbol = true;
      field.validations.tableName = true;
      field.validations.colName = true;
      this.setState({ localData: data });
      return;
    }

    // Fixed value or default
    field.tableName = "";
    field.tableAlias = "";
    field.tableId = null;
    field.tableSchema = "";
    field.colName = "";
    field.colSqlDataType = "";

    const symbolToSave = _.cloneDeep(symbol);
    if (!symbolToSave.label && symbolToSave.description) symbolToSave.label = symbolToSave.description;
    if (!symbolToSave.description && symbolToSave.label) symbolToSave.description = symbolToSave.label;
    if (!symbolToSave.label && !symbolToSave.description) {
      symbolToSave.label = "Fixed Value";
      symbolToSave.description = "Fixed Value";
    }

    if (symbol.category === ESymbolCategory.fixed) {
      symbolToSave.isFixed = true;
    }

    field.paramSymbol = symbolToSave;

    if (!field.validations) field.validations = {};

    if (symbol.category === ESymbolCategory.fixed) {
      const hasFixedValue = symbol.fixedValue !== undefined && symbol.fixedValue !== null && symbol.fixedValue !== "";
      const isNotPlaceholder = !!(symbol.label && symbol.label !== "Fixed Value");
      const isValidFixedValue = hasFixedValue || isNotPlaceholder;
      field.validations.paramSymbol = isValidFixedValue;
      field.validations.tableName = true;
      field.validations.colName = true;
    } else if (symbol.category === ESymbolCategory.default) {
      const hasDefaultValue =
        symbol.defaultValueName !== undefined && symbol.defaultValueName !== null && symbol.defaultValueName !== "";
      field.validations.paramSymbol = hasDefaultValue;
      field.validations.tableName = true;
      field.validations.colName = true;
    } else if (symbol.category === ESymbolCategory.sqlParameter) {
      const hasSqlParamName =
        symbol.sqlParamName !== undefined && symbol.sqlParamName !== null && symbol.sqlParamName !== "";
      field.validations.paramSymbol = hasSqlParamName;
      field.validations.tableName = true;
      field.validations.colName = true;
    } else {
      field.validations.paramSymbol = true;
      field.validations.tableName = true;
      field.validations.colName = true;
    }

    this.setState({ localData: data });
  };

  getFunctionName = (): string => {
    const data = this.getData();
    switch (data.function) {
      case ECalculatedFieldFunction.DateDiff:
        return "DATEDIFF";
      case ECalculatedFieldFunction.RowNumber:
        return "ROW_NUMBER";
      default:
        return "Function";
    }
  };

  createEmptyField = (withSortType: boolean = false): IGenericSelectDataField => ({
    tableName: "",
    tableSchema: "",
    tableId: null,
    colName: "",
    colSqlDataType: "",
    alias: "",
    paramSymbol: { category: ESymbolCategory.fixed } as ISymbol,
    ...(withSortType ? { sortType: ESortType.ascending } : {})
  });

  handleSelectDateDiffPart = (value: EDateDiffPart) => {
    const data = _.cloneDeep(this.getData());
    data.dateDiffPart = value;
    this.setState({ localData: data });
  };

  handleFieldSortTypeSelect = (sortType: ESortType, fieldIndex: number) => {
    const data = _.cloneDeep(this.getData());
    if (!data.orderByFields) return;
    data.orderByFields[fieldIndex].sortType = sortType;
    this.setState({ localData: data });
  };

  handleAliasChange = (value: string) => {
    const data = _.cloneDeep(this.getData());
    data.alias = value;
    if (!data.validations) data.validations = {};
    data.validations.alias = value !== undefined && value.trim().length > 0;
    this.setState({ localData: data });
  };

  handleSelectGroupBy = (value: EGenericSelectDataGroupBy | undefined) => {
    const data = _.cloneDeep(this.getData());
    data.groupBy = value;
    this.setState({ localData: data });
  };

  getValidationSuffixIcon = (hasError: boolean, hasValue: boolean) => {
    if (hasError && !hasValue) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <KuikaSvg name="alert-circle" width={16} height={16} color="var(--red-5)" />
          <KuikaSvg name="chevron-down" type="arrows" width={12} height={12} color="rgba(96, 111, 133, 1)" />
        </div>
      );
    }
    return undefined;
  };

  getValidationRightIcon = (hasError: boolean, hasValue: boolean) => {
    if (hasError && !hasValue) {
      return <KuikaSvg name="alert-circle" width={16} height={16} color="var(--red-5)" />;
    }
    return undefined;
  };

  handleAddField = (fieldArray: "partitionByFields") => {
    const data = _.cloneDeep(this.getData());
    if (!data[fieldArray]) data[fieldArray] = [];
    data[fieldArray].push(this.createEmptyField(false));
    this.setState({ localData: data });
  };

  // Check if a field is filled (either table field or has valid selection)
  isFieldFilled = (field: IGenericSelectDataField): boolean => {
    // Field is filled if it has tableName and colName (table field)
    if (field.tableName && field.tableName.trim() !== "" && field.colName && field.colName.trim() !== "") {
      return true;
    }
    // Or if it has paramSymbol with actionField category and fieldName (selected from table)
    if (
      field.paramSymbol &&
      field.paramSymbol.category === ESymbolCategory.actionField &&
      field.paramSymbol.fieldName
    ) {
      return true;
    }
    // Or if it has paramSymbol with fixed value category and actual value
    if (
      field.paramSymbol &&
      field.paramSymbol.category === ESymbolCategory.fixed &&
      field.paramSymbol.fixedValue != null &&
      field.paramSymbol.fixedValue !== ""
    ) {
      return true;
    }
    // Or if it has paramSymbol with default value category (like "now", "empty", etc.)
    if (
      field.paramSymbol &&
      field.paramSymbol.category === ESymbolCategory.default &&
      field.paramSymbol.defaultValueName
    ) {
      return true;
    }
    return false;
  };

  // Check if all fields in an array are filled
  areAllFieldsFilled = (fields: IGenericSelectDataField[] | undefined): boolean => {
    if (!fields || fields.length === 0) return true;
    return fields.every((field) => this.isFieldFilled(field));
  };

  handleRemoveField = (fieldIndex: number, fieldArray: "partitionByFields") => {
    const data = _.cloneDeep(this.getData());
    (data[fieldArray] as IGenericSelectDataField[]).splice(fieldIndex, 1);
    this.setState({ localData: data });
  };

  renderFieldSelector = (
    field: IGenericSelectDataField,
    index: number,
    fieldArray: "fields" | "partitionByFields" | "orderByFields",
    label?: string,
    showSortType: boolean = false,
    valueTypeOverride?: BackEndValueType
  ) => {
    const { isEditing } = this.state;
    return (
      <div className="calculated-field-row">
        {label && (
          <div className="calculated-field-row__label">
            <KuikaText className="calculated-fields-item__label-text">{label}</KuikaText>
          </div>
        )}
        <div className="calculated-field-row__content">
          <div
            className={`calculated-field-row__picker ${
              field.validations && Object.values(field.validations).some((v) => v === false)
                ? "has-validation-error"
                : ""
            }`}
          >
            <SymbolPicker
              key={`${fieldArray}-${index}-${field.tableName || ""}-${field.colName || ""}`}
              symbol={this.getFieldSymbol(field)}
              valueType={valueTypeOverride || this.getFieldValueType(field)}
              onSelectedSymbol={(symbol) => this.handleFieldSymbolSelected(symbol, index, fieldArray)}
              hideActionResults
              showInSingleLine
              isGenericSelect
              genericSelectTables={this.props.selectedTables}
              genericSelectTableModels={this.props.tables}
              hideFixedValue={fieldArray === "partitionByFields" || fieldArray === "orderByFields"}
              disabled={!isEditing}
            />
          </div>
          {showSortType && (
            <div className="calculated-field-row__sort-type">
              <KuikaSelectbox
                options={sortTypeOptions}
                value={field.sortType}
                onChange={(val) => this.handleFieldSortTypeSelect(val, index)}
                disabled={!isEditing}
                placeholder="Sort"
                suffixIcon={this.getValidationSuffixIcon(field.validations?.sortType === false, !!field.sortType)}
              />
            </div>
          )}
          {fieldArray === "partitionByFields" && isEditing && (
            <KuikaButton
              isIcon
              size="sm"
              type="text"
              onClick={() => this.handleRemoveField(index, "partitionByFields")}
            >
              <KuikaSvg name="trash" width={16} height={16} color="var(--red-5)" pointer />
            </KuikaButton>
          )}
        </div>
      </div>
    );
  };

  renderDateDiffFields = () => {
    const data = this.getData();
    const { isEditing } = this.state;

    return (
      <>
        <div className="calculated-fields-item__section">
          <div className="calculated-fields-item__label">
            <KuikaText className="calculated-fields-item__label-text">Select Date Part</KuikaText>
          </div>
          <KuikaSelectbox
            options={datePartOptions}
            value={data.dateDiffPart}
            onChange={this.handleSelectDateDiffPart}
            placeholder="Select Date Part"
            disabled={!isEditing}
            suffixIcon={undefined}
          />
        </div>

        <div className="calculated-fields-item__section" style={{ gap: "var(--gap-s)" }}>
          <KuikaH7 className="calculated-fields-item__date-fields-title">DATE FIELDS</KuikaH7>
          <div className="calculated-fields-item__date-fields-row">
            {(data.fields || []).map((field, index) => (
              <div key={index} className="calculated-fields-item__date-field">
                {this.renderFieldSelector(
                  field,
                  index,
                  "fields",
                  index === 0 ? "Start Date" : "End Date",
                  false,
                  "datetimeoffset" as BackEndValueType
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="calculated-fields-item__row">
          <div className="calculated-fields-item__field-group">
            <div className="calculated-fields-item__label">
              <KuikaText className="calculated-fields-item__label-text">Group By</KuikaText>
            </div>
            <KuikaSelectbox
              options={groupByOptions}
              value={data.groupBy}
              onChange={this.handleSelectGroupBy}
              placeholder="Group By"
              disabled={!isEditing}
              allowClear
              onClear={() => this.handleSelectGroupBy(undefined)}
              suffixIcon={this.getValidationSuffixIcon(data.validations?.groupBy === false, !!data.groupBy)}
            />
          </div>
          <div className="calculated-fields-item__field-group">
            <div className="calculated-fields-item__label">
              <KuikaText className="calculated-fields-item__label-text">Alias</KuikaText>
            </div>
            <KuikaInput
              placeholder="Alias"
              value={data.alias}
              onChange={(_, val) => this.handleAliasChange(val as string)}
              disabled={!isEditing}
              className={data.validations?.alias === false ? "has-validation-error" : ""}
              rightIcon={this.getValidationRightIcon(data.validations?.alias === false, !!data.alias)}
            />
          </div>
        </div>
      </>
    );
  };

  renderRowNumberFields = () => {
    const data = this.getData();
    const { isEditing } = this.state;
    // RowNumber logic: Order By (Required, 1 field), Partition By (Optional, dynamic).
    // Note: data.orderByFields is used for Order By. data.partitionByFields for Partition By.

    return (
      <>
        {/* Order By - Required */}
        <div className="calculated-fields-item__section">
          <div className="calculated-fields-item__label">
            <KuikaText className="calculated-fields-item__label-text">Order By *</KuikaText>
          </div>
          {(data.orderByFields || []).map((field, index) =>
            this.renderFieldSelector(field, index, "orderByFields", undefined, true)
          )}
        </div>

        {/* Partition By - Optional (only one field allowed) */}
        <div className="calculated-fields-item__section">
          <div className="calculated-fields-item__partition-header">
            <KuikaText className="calculated-fields-item__label-text">Partition By (Optional)</KuikaText>
          </div>
          {(data.partitionByFields || []).map((field, index) =>
            this.renderFieldSelector(field, index, "partitionByFields")
          )}
          {isEditing && (!data.partitionByFields || data.partitionByFields.length === 0) && (
            <KuikaButton
              style={{ width: "fit-content", marginTop: 4 }}
              size="sm"
              type="text"
              onClick={() => this.handleAddField("partitionByFields")}
              uppercase
            >
              + Add
            </KuikaButton>
          )}
        </div>

        {/* Alias */}
        <div className="calculated-fields-item__field-group">
          <div className="calculated-fields-item__label">
            <KuikaText className="calculated-fields-item__label-text">Alias</KuikaText>
          </div>
          <KuikaInput
            placeholder="Alias"
            value={data.alias}
            onChange={(_, val) => this.handleAliasChange(val as string)}
            disabled={!isEditing}
            className={data.validations?.alias === false ? "has-validation-error" : ""}
            rightIcon={this.getValidationRightIcon(data.validations?.alias === false, !!data.alias)}
          />
        </div>
      </>
    );
  };

  render() {
    const data = this.getData();
    const { isEditing } = this.state;

    return (
      <div className="calculated-fields-item">
        {/* Header */}
        {!isEditing && (
          <div className="calculated-fields-item__header">
            <KuikaSvg name="edit" width={16} height={16} pointer onClick={this.handleEnterEdit} />
            <KuikaSvg name="trash" width={16} height={16} color="var(--red-5)" pointer onClick={this.props.delete} />
          </div>
        )}

        {/* Body */}
        {data.function === ECalculatedFieldFunction.DateDiff && this.renderDateDiffFields()}
        {data.function === ECalculatedFieldFunction.RowNumber && this.renderRowNumberFields()}

        {/* Actions */}
        {isEditing && (
          <div className="calculated-fields-item__actions">
            <KuikaButton size="sm" type="text" onClick={this.handleCancel} uppercase>
              Cancel
            </KuikaButton>
            <KuikaButton size="sm" type="primary" onClick={this.handleSave} uppercase>
              Update
            </KuikaButton>
          </div>
        )}
      </div>
    );
  }
}

export const CalculatedFieldsItemComponent = CalculatedFieldsItem;
